import { create } from "zustand";
import type { KitchenOrder } from "@shared/types/order";
import type { KitchenStation } from "@shared/types/station";

export interface DishStockInfo {
  isLowStock: boolean;
  isOutOfStock: boolean;
  currentStock: number;
}

interface KitchenState {
  clientId: string | null;
  jwtToken: string | null;
  userName: string | null;
  stations: KitchenStation[];
  activeOrders: KitchenOrder[];
  audioEnabled: boolean;
  stationOrder: string[];
  /** Map of dishId -> stock info, updated via WebSocket inventory events */
  dishInventory: Record<string, DishStockInfo>;
  /** KDS board view mode — persisted to localStorage key "kitchen_viewMode" */
  viewMode: "station" | "table";
  /**
   * Optional station scope for the per-table board (`?station=<id>`), used by
   * single-station monitors such as the bar. Persisted to localStorage key
   * "kitchen_stationFilter"; null = whole kitchen board.
   */
  stationFilter: string | null;
  /**
   * Feature 109 — ordini con ondate appena spostate (badge temporaneo KDS).
   * Map orderId -> { targetWave, at(ms) }. La freschezza (<6s) è valutata dal
   * renderer col tick `now`; le entry vecchie sono garbage-collected al
   * markWaveChange successivo.
   */
  recentWaveChanges: Record<string, { targetWave: number; at: number }>;
  /**
   * Feature 113 — Blocco ordini cibo dallo chef.
   * `enabled` = disponibilità funzionalità (mostra/nasconde il toggle).
   * `active`  = stato runtime del blocco.
   */
  foodBlock: { enabled: boolean; active: boolean };

  // Actions
  setClientId: (id: string | null) => void;
  setJwtToken: (token: string | null) => void;
  setUserName: (name: string | null) => void;
  setStations: (stations: KitchenStation[]) => void;
  setActiveOrders: (orders: KitchenOrder[]) => void;
  upsertOrder: (order: KitchenOrder) => void;
  removeOrder: (orderId: string) => void;
  updateItemStatus: (itemId: string, status: string) => void;
  toggleAudio: () => void;
  setStationOrder: (order: string[]) => void;
  moveStation: (stationId: string, direction: "left" | "right") => void;
  setDishStock: (dishId: string, info: DishStockInfo | null) => void;
  setViewMode: (mode: "station" | "table") => void;
  /** Feature 109 — registra lo spostamento ondate per il badge temporaneo. */
  markWaveChange: (orderId: string, targetWave: number) => void;
  /** Feature 113 — aggiorna lo stato del blocco ordini cibo. */
  setFoodBlock: (state: { enabled: boolean; active: boolean }) => void;
  logout: () => void;
}

/** Finestra di visibilità del badge "spostato in Nª ondata" (FR-006a). */
export const WAVE_CHANGE_BADGE_MS = 6000;

function readViewMode(): "station" | "table" {
  // Feature 084 — il query param `?view=table|station` ha priorità (usato
  // nell'URL di autostart del kiosk Raspberry per forzare la vista per-tavolo
  // di default). Se presente viene anche persistito in localStorage così resta
  // dopo refresh che non passano il param.
  try {
    const param = new URLSearchParams(window.location.search)
      .get("view")
      ?.toLowerCase();
    if (param === "table" || param === "station") {
      localStorage.setItem("kitchen_viewMode", param);
      return param;
    }
  } catch {
    // window/URL non disponibili — ignora e usa il fallback localStorage
  }
  const stored = localStorage.getItem("kitchen_viewMode");
  return stored === "table" ? "table" : "station";
}

function readStationFilter(): string | null {
  // `?station=<id>` scopes the per-table board to one station (bar monitor).
  // Like `?view=`, the query param wins and is persisted; `?station=` (empty)
  // clears the stored scope.
  try {
    const params = new URLSearchParams(window.location.search);
    if (params.has("station")) {
      const value = params.get("station")?.trim() ?? "";
      if (value) {
        localStorage.setItem("kitchen_stationFilter", value);
        return value;
      }
      localStorage.removeItem("kitchen_stationFilter");
      return null;
    }
  } catch {
    // window/URL not available — fall back to localStorage
  }
  return localStorage.getItem("kitchen_stationFilter") || null;
}

export const useKitchenStore = create<KitchenState>((set) => ({
  clientId: localStorage.getItem("kitchen_clientId"),
  jwtToken: localStorage.getItem("kitchen_jwt"),
  userName: localStorage.getItem("kitchen_userName"),
  stations: [],
  activeOrders: [],
  audioEnabled: true,
  stationOrder: JSON.parse(
    localStorage.getItem("kitchen_stationOrder") || "[]",
  ),
  dishInventory: {},
  viewMode: readViewMode(),
  stationFilter: readStationFilter(),
  recentWaveChanges: {},
  foodBlock: { enabled: false, active: false },

  setClientId: (id) => {
    if (id) localStorage.setItem("kitchen_clientId", id);
    else localStorage.removeItem("kitchen_clientId");
    set({ clientId: id });
  },

  setJwtToken: (token) => {
    if (token) localStorage.setItem("kitchen_jwt", token);
    else localStorage.removeItem("kitchen_jwt");
    set({ jwtToken: token });
  },

  setUserName: (name) => {
    if (name) localStorage.setItem("kitchen_userName", name);
    else localStorage.removeItem("kitchen_userName");
    set({ userName: name });
  },

  setStations: (stations) => set({ stations }),

  setActiveOrders: (orders) => set({ activeOrders: orders }),

  upsertOrder: (order) =>
    set((state) => {
      const idx = state.activeOrders.findIndex((o) => o.id === order.id);
      if (idx >= 0) {
        const updated = [...state.activeOrders];
        updated[idx] = order;
        return { activeOrders: updated };
      }
      return { activeOrders: [order, ...state.activeOrders] };
    }),

  removeOrder: (orderId) =>
    set((state) => ({
      activeOrders: state.activeOrders.filter((o) => o.id !== orderId),
    })),

  markWaveChange: (orderId, targetWave) =>
    set((state) => {
      const now = Date.now();
      // GC: tieni solo le entry ancora dentro una finestra ampia (30s).
      const next: Record<string, { targetWave: number; at: number }> = {};
      for (const [oid, entry] of Object.entries(state.recentWaveChanges)) {
        if (now - entry.at < 30_000) next[oid] = entry;
      }
      next[orderId] = { targetWave, at: now };
      return { recentWaveChanges: next };
    }),

  updateItemStatus: (itemId, status) =>
    set((state) => ({
      activeOrders: state.activeOrders.map((order) => ({
        ...order,
        items: order.items.map((item) =>
          item.id === itemId ? { ...item, status: status as any } : item,
        ),
      })),
    })),

  toggleAudio: () => set((state) => ({ audioEnabled: !state.audioEnabled })),

  setFoodBlock: (fb) => set({ foodBlock: fb }),

  setDishStock: (dishId, info) =>
    set((state) => {
      if (info === null) {
        // Remove stale entry when inventory tracking is disabled
        const { [dishId]: _removed, ...rest } = state.dishInventory;
        return { dishInventory: rest };
      }
      return { dishInventory: { ...state.dishInventory, [dishId]: info } };
    }),

  setStationOrder: (order) => {
    localStorage.setItem("kitchen_stationOrder", JSON.stringify(order));
    set({ stationOrder: order });
  },

  setViewMode: (mode) => {
    localStorage.setItem("kitchen_viewMode", mode);
    set({ viewMode: mode });
  },

  moveStation: (stationId, direction) =>
    set((state) => {
      const order = [...state.stationOrder];
      const idx = order.indexOf(stationId);
      if (idx < 0) return state;
      const swapIdx = direction === "left" ? idx - 1 : idx + 1;
      if (swapIdx < 0 || swapIdx >= order.length) return state;
      [order[idx], order[swapIdx]] = [order[swapIdx], order[idx]];
      localStorage.setItem("kitchen_stationOrder", JSON.stringify(order));
      return { stationOrder: order };
    }),

  logout: () => {
    localStorage.removeItem("kitchen_jwt");
    localStorage.removeItem("kitchen_clientId");
    localStorage.removeItem("kitchen_userName");
    localStorage.removeItem("kitchen_stationOrder");
    localStorage.removeItem("kitchen_viewMode");
    localStorage.removeItem("kitchen_stationFilter");
    set({
      jwtToken: null,
      clientId: null,
      userName: null,
      stations: [],
      activeOrders: [],
      stationOrder: [],
      dishInventory: {},
      viewMode: "station",
      stationFilter: null,
    });
  },
}));
